import { useMemo } from 'react';
import { useAppSelector, useMainTimePeriod } from 'src/utils/Hooks/typedHooks';
import { useBaseMetricRequestBuilder } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useBaseMetricRequestBuilder';
import useGenericAdvancedSearch from 'src/utils/Hooks/useGenericAdvancedSearch';
import _get from 'lodash/get';

interface UseSalesDataProps {
  retailerSku?: string;
  stacklineSku?: string;
}

export const useSalesData = ({ retailerSku, stacklineSku }: UseSalesDataProps) => {
  const { startWeek, endWeek } = useMainTimePeriod();
  const mainEntity = useAppSelector((state) => state.entityService.mainEntity);
  
  const requestId = `sku-sales-${retailerSku || stacklineSku || 'unknown'}`;
  
  const requestBuilder = useBaseMetricRequestBuilder({
    requestId,
    startWeekId: startWeek,
    endWeekId: endWeek,
    groupByFieldName: 'stacklineSku',
    fields: [
      {
        name: 'retailSales'
      },
      {
        name: 'unitsSold'
      }
    ],
    indexName: 'sales'
  });

  // Add condition filter based on available SKU
  if (stacklineSku) {
    requestBuilder.addConditionTermFilter('stacklineSku', 'should', [stacklineSku]);
  } else if (retailerSku) {
    requestBuilder.addConditionTermFilter('retailerSku', 'should', [retailerSku]);
  }

  const requestBody = useMemo(() => {
    return requestBuilder.build();
  }, [requestBuilder]);

  const { data: salesResponse, isLoading } = useGenericAdvancedSearch({
    requestId,
    queryKeys: [requestBody],
    requestBody: [requestBody],
    shouldPerformFetch: !!(retailerSku || stacklineSku)
  });

  const salesData = useMemo(() => {
    if (!salesResponse?.data?.[0]?.aggregations?.by_stacklineSku?.[0]) {
      return null;
    }

    const aggregation = salesResponse.data[0].aggregations.by_stacklineSku[0];
    return {
      retailSales: _get(aggregation, 'additionalValues.retailSales_sum_value', 0),
      unitsSold: _get(aggregation, 'additionalValues.unitsSold_sum_value', 0)
    };
  }, [salesResponse]);

  return {
    salesData,
    isLoading,
    error: !salesData && !isLoading ? 'Failed to fetch sales data' : null
  };
}; 